import { useMemo, useState } from 'react';
import {
  Undo2, Receipt, CalendarDays, TriangleAlert, RotateCcw, Package, PackageX,
  CircleCheck, History, Clock, Wallet,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { openBillPdf } from '@/features/billing/api';
import { PrintBillButton } from '@/features/printing/PrintBillButton';
import {
  useOutpatientSales, useOutpatientSale, useCreatePharmacyReturn, usePharmacyReturns,
  useSettlePharmacyRefund,
} from '../useOutpatient';
// PaymentModePicker / PaymentStatusPicker are the SAME controls the sale
// screen uses — a refund is a payment, so it gets the payment module's own
// two fields rather than a parallel pair that could drift.
import {
  Empty, TotalLine, PaymentModePicker, PaymentStatusPicker,
  money, moneyWhole, shortDateTime, timeOnly,
} from './ui';
import type { OutpatientSaleLine, PaymentMode, SaleStatus } from '../types';

const today = () => new Date().toISOString().slice(0, 10);

const STATUS: Record<SaleStatus, { label: string; variant: 'success' | 'warn' | 'danger' }> = {
  completed: { label: 'Completed', variant: 'success' },
  partially_returned: { label: 'Partly returned', variant: 'warn' },
  cancelled: { label: 'Fully returned', variant: 'danger' },
};

/** One line's draft return state — how many units, and whether they go back on the shelf. */
interface ReturnDraft {
  qty: number;
  restock: boolean;
}

/**
 * The day's pharmacy sales, and the form for taking medicine back.
 *
 * Two rules drive the whole shape of this screen, and both are non-negotiable
 * rather than design preferences:
 *
 *   1. **The bill is never deleted or edited.** GST law requires the invoice
 *      serial run to stay unbroken, and the customer is holding the original.
 *      A return is therefore its own numbered document (PHRET/…), and the sale
 *      is only *marked* partly returned or fully returned.
 *
 *   2. **A unit is refunded at what it was sold for, not at MRP.** If it went
 *      out at 10% off, that is what comes back — with the GST that was actually
 *      charged on it. Refunding MRP on a discounted sale hands out money the
 *      hospital never took.
 *
 * "Put back on the shelf" is a separate decision per line, on purpose: an
 * opened strip is still refunded to the customer but must not be re-sold, and
 * conflating the two is how expired or tampered stock quietly re-enters
 * inventory.
 */
export default function SalesReturnsTab() {
  const [date, setDate] = useState(today());
  const [channel, setChannel] = useState<'outpatient' | 'admission' | 'all'>('outpatient');
  const [openSaleId, setOpenSaleId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, ReturnDraft>>({});
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');
  const [justReturned, setJustReturned] = useState<{ number: string; adjusted: number; refund: number } | null>(null);
  const [refundMode, setRefundMode] = useState<PaymentMode>('Cash');
  const [refundStatus, setRefundStatus] = useState<'Paid' | 'Pending'>('Paid');

  const salesQuery = useOutpatientSales({ date, channel });
  const saleQuery = useOutpatientSale(openSaleId);
  const returnsQuery = usePharmacyReturns({ date });
  // Outstanding refunds are NOT date-filtered — one raised on Monday still has
  // to be paid out on Thursday, and a list that hid it would be worse than no
  // list at all.
  const pendingQuery = usePharmacyReturns({ status: 'Pending' });
  const settleRefund = useSettlePharmacyRefund();
  const createReturn = useCreatePharmacyReturn();

  const sales = salesQuery.data ?? [];
  const sale = saleQuery.data;
  const returns = returnsQuery.data ?? [];

  const dayTotal = sales.reduce((s, b) => s + parseFloat(b.total_amount), 0);
  // Only refunds actually PAID leave the till. A pending one is a liability,
  // not a cash movement, so counting it here would make the drawer reconcile
  // short by exactly the amount nobody has handed over yet.
  const dayRefunds = returns.filter((r) => r.refund_status === 'Paid').reduce((s, r) => s + parseFloat(r.refund_amount), 0);

  const openSale = (id: number) => {
    setOpenSaleId(id);
    setDrafts({});
    setReason('');
    setFormError('');
    setJustReturned(null);
    setRefundStatus('Paid');
    // Money usually goes back the way it came — and for a card payment it
    // legally has to — so the sale's own mode is the default rather than Cash.
    const sold = sales.find((x) => x.id === id);
    if (sold) setRefundMode(sold.payment_mode);
  };

  /** Units still returnable on a line: what was sold, less what already came back. */
  const returnableOf = (line: OutpatientSaleLine) => line.quantity - (line.returned_quantity || 0);

  const setQty = (line: OutpatientSaleLine, qty: number) => {
    const clamped = Math.max(0, Math.min(returnableOf(line), qty));
    setDrafts((prev) => ({ ...prev, [line.id]: { qty: clamped, restock: prev[line.id]?.restock ?? true } }));
  };

  const setRestock = (lineId: number, restock: boolean) =>
    setDrafts((prev) => ({ ...prev, [lineId]: { qty: prev[lineId]?.qty ?? 0, restock } }));

  const selected = useMemo(() => Object.entries(drafts).filter(([, d]) => d.qty > 0), [drafts]);

  // Live refund estimate from the line's own snapshotted figures — the same
  // arithmetic the server does, shown so the operator can tell the customer the
  // number before committing rather than after.
  const estimatedRefund = useMemo(() => {
    if (!sale) return 0;
    return selected.reduce((sum, [lineId, d]) => {
      const line = sale.items.find((l) => l.id === Number(lineId));
      if (!line) return sum;
      const unitNet = parseFloat(line.line_total) / line.quantity;
      const unitGst = parseFloat(line.gst_amount || '0') / line.quantity;
      return sum + (unitNet + unitGst) * d.qty;
    }, 0);
  }, [sale, selected]);

  /** "Return everything still returnable" — the single most common whole-bill case. */
  const returnAll = () => {
    if (!sale) return;
    const next: Record<number, ReturnDraft> = {};
    sale.items.forEach((l) => {
      const returnable = returnableOf(l);
      if (returnable > 0) next[l.id] = { qty: returnable, restock: drafts[l.id]?.restock ?? true };
    });
    setDrafts(next);
  };

  const submitReturn = () => {
    if (!sale) return;
    if (!reason.trim()) { setFormError('A reason for the return is required.'); return; }
    if (!selected.length) { setFormError('Enter a quantity on at least one line.'); return; }
    setFormError('');
    createReturn.mutate(
      {
        saleId: sale.id,
        input: {
          reason: reason.trim(),
          refund_mode: refundMode,
          refund_status: refundStatus,
          items: selected.map(([lineId, d]) => ({
            bill_item_id: Number(lineId),
            quantity: d.qty,
            restock: d.restock,
          })),
        },
      },
      {
        onSuccess: (ret) => {
          setDrafts({}); setReason('');
          setJustReturned({ number: ret.return_number, adjusted: parseFloat(ret.adjusted_amount || '0'), refund: parseFloat(ret.refund_amount) });
        },
      }
    );
  };

  const returnError = createReturn.error instanceof ApiError ? createReturn.error.message : null;
  const settleError = settleRefund.error instanceof ApiError ? settleRefund.error.message : null;
  const pending = pendingQuery.data ?? [];
  const pendingTotal = pending.reduce((sum, r) => sum + parseFloat(r.refund_amount), 0);
  const fullyReturned = sale?.sale_status === 'cancelled';

  return (
    <div className="space-y-4">
      {/* ── Day header ──────────────────────────────────────────── */}
      <Card>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FormField label="Date">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input
                type="date"
                className="w-auto pl-8"
                value={date}
                onChange={(e) => { setDate(e.target.value); setOpenSaleId(null); }}
              />
            </div>
          </FormField>

          <div>
            <div className="lbl">Show</div>
            {/* Counter sales are the default view, but the till reconciles against
                ALL pharmacy money taken that day — visit-linked dispensing lands
                in the same drawer. */}
            <div className="inline-flex rounded-lg border border-border p-0.5">
              {([['outpatient', 'Counter'], ['admission', 'IP'], ['all', 'All pharmacy']] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setChannel(id); setOpenSaleId(null); }}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                    channel === id ? 'bg-teal text-white' : 'text-ink-muted hover:bg-muted'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid3 mt-4">
          <StatCard accentSide="left" accentColor="var(--teal)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Billed</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{moneyWhole(dayTotal)}</div>
            <div className="mt-1 text-[11px] text-ink-muted">{sales.length} sale{sales.length === 1 ? '' : 's'}</div>
          </StatCard>
          <StatCard accentSide="left" accentColor="var(--color-danger)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Refunded</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{dayRefunds > 0 ? moneyWhole(dayRefunds) : '—'}</div>
            <div className="mt-1 text-[11px] text-ink-muted">{returns.length} return{returns.length === 1 ? '' : 's'}</div>
          </StatCard>
          <StatCard accentSide="left" accentColor="var(--color-success)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">In the till</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{moneyWhole(dayTotal - dayRefunds)}</div>
            <div className="mt-1 text-[11px] text-ink-muted">billed less refunds</div>
          </StatCard>
        </div>
      </Card>

      {/* ── Sales ↔ detail ──────────────────────────────────────── */}
      <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <Card>
          <CardHeader>
            <CardTitle><Receipt size={15} /> Sales</CardTitle>
            {sales.length > 0 && <Badge variant="neutral">{sales.length}</Badge>}
          </CardHeader>

          {salesQuery.isLoading ? (
            <LoadingState />
          ) : sales.length === 0 ? (
            <Empty label="No sales on this date" hint="Pick another date, or switch to All pharmacy." />
          ) : (
            <div className="space-y-1.5">
              {sales.map((s) => {
                const active = openSaleId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => openSale(s.id)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
                      active ? 'border-teal bg-accent' : 'border-border hover:bg-muted'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-semibold">{s.patient_name}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-ink-muted">
                          <span className="font-mono">{s.bill_number}</span>
                          <span>·</span>
                          <span>{timeOnly(s.created_at)}</span>
                          <span>·</span>
                          <span>{s.payment_mode}</span>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-[13px] font-bold tabular-nums">{money(s.total_amount)}</div>
                        <Badge variant={STATUS[s.sale_status].variant} className="mt-1">{STATUS[s.sale_status].label}</Badge>
                      </div>
                    </div>
                    {(s.sale_channel === 'visit' || parseFloat(String(s.discount_amount)) > 0 || s.loyalty_points_redeemed > 0 || s.loyalty_points_earned > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {s.sale_channel === 'visit' && <Badge variant="info">From a visit</Badge>}
                        {parseFloat(String(s.discount_amount)) > 0 && <Badge variant="neutral">Disc {money(s.discount_amount)}</Badge>}
                        {s.loyalty_points_redeemed > 0 && <Badge variant="neutral">−{s.loyalty_points_redeemed} pts</Badge>}
                        {s.loyalty_points_earned > 0 && <Badge variant="neutral">+{s.loyalty_points_earned} pts</Badge>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* ── Detail / return form ──────────────────────────────── */}
        {!openSaleId ? (
          <Card className="min-[1100px]:sticky min-[1100px]:top-4">
            <Empty
              label="Open a sale to take medicine back"
              hint="Pick one on the left. The original bill is never edited — a return gets its own PHRET document."
              className="py-12"
            />
          </Card>
        ) : (
          <Card className="min-[1100px]:sticky min-[1100px]:top-4">
            {saleQuery.isLoading || !sale ? (
              <LoadingState />
            ) : (
              <>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle><Undo2 size={15} /> {sale.bill_number}</CardTitle>
                    <div className="mt-1 text-[11.5px] text-ink-muted">
                      {sale.patient_name} · <span className="font-mono">{sale.uhid}</span> · {shortDateTime(sale.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { void openBillPdf(sale.id); }}>
                      <Receipt size={13} /> Invoice
                    </Button>
                    <PrintBillButton billId={sale.id} />
                  </div>
                </CardHeader>

                {sale.prescription_skipped != null && (
                  <div className="-mt-1.5 mb-3 text-[11.5px] text-ink-muted">
                    {sale.prescription_skipped
                      ? `Sold over the counter — ${sale.skip_reason}`
                      : `Prescribed by Dr. ${sale.prescriber_name}${sale.prescriber_hospital ? ` · ${sale.prescriber_hospital}` : ''}`}
                  </div>
                )}

                {justReturned && (
                  <div className="mb-3 flex items-center gap-2.5 rounded-lg bg-success-soft px-3 py-2.5 text-[12px] text-success-ink">
                    <CircleCheck size={15} className="shrink-0" />
                    <span>
                      Return <strong>{justReturned.number}</strong> recorded.
                      {justReturned.adjusted > 0 && <> ₹{justReturned.adjusted.toFixed(2)} taken off what is still owed on this bill.</>}
                      {justReturned.refund > 0 ? <> ₹{justReturned.refund.toFixed(2)} to refund.</> : justReturned.adjusted > 0 ? <> Nothing to refund.</> : null}
                    </span>
                  </div>
                )}

                {(returnError || formError) && (
                  <div className="mb-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                    <TriangleAlert size={15} className="mt-px shrink-0" />
                    <p className="leading-relaxed">{returnError || formError}</p>
                  </div>
                )}

                {fullyReturned ? (
                  <div className="mb-3 flex items-center gap-2.5 rounded-lg bg-muted px-3 py-2.5 text-[12px] text-ink-muted">
                    <RotateCcw size={15} className="shrink-0" />
                    <span>Everything on this bill has already been returned.</span>
                  </div>
                ) : (
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                      What is coming back
                    </div>
                    <Button variant="ghost" size="sm" onClick={returnAll}>Return everything</Button>
                  </div>
                )}

                {/* Lines. A card list rather than a table: each line carries a
                    quantity control and a restock decision, and a table cell is
                    the wrong container for two interactive controls at this
                    width. The read-only summary of a returned bill still reads
                    fine as rows. */}
                <div className="divide-y divide-line">
                  {sale.items.map((line) => {
                    const returnable = returnableOf(line);
                    const draft = drafts[line.id];
                    // Per-unit refund, from this line's own snapshotted numbers —
                    // the discounted price, never MRP.
                    const unitRefund = (parseFloat(line.line_total) + parseFloat(line.gst_amount || '0')) / line.quantity;
                    const picked = (draft?.qty ?? 0) > 0;
                    return (
                      <div key={line.id} className={cn('py-2.5 first:pt-0', picked && 'bg-warn-soft/30')}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-[12.5px] font-semibold">{line.description}</div>
                            <div className="mt-0.5 text-[11px] text-ink-muted">
                              {line.quantity} × {money(unitRefund)}
                              {parseFloat(line.discount_percent) > 0 && ` · after ${parseFloat(line.discount_percent)}% off`}
                              {line.batch_number && ` · batch ${line.batch_number}`}
                            </div>
                            {line.returned_quantity > 0 && (
                              <Badge variant="neutral" className="mt-1">{line.returned_quantity} already returned</Badge>
                            )}
                          </div>
                          <div className="shrink-0 text-right text-[12.5px] font-semibold tabular-nums">
                            {money(unitRefund * line.quantity)}
                          </div>
                        </div>

                        {!fullyReturned && returnable > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                            <label className="flex items-center gap-1.5">
                              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Return</span>
                              <Input
                                type="number"
                                className="h-7 w-16 text-[13px] tabular-nums"
                                min={0}
                                max={returnable}
                                value={draft?.qty ?? ''}
                                placeholder="0"
                                onChange={(e) => setQty(line, parseInt(e.target.value, 10) || 0)}
                              />
                              <span className="text-[11px] text-ink-muted">of {returnable}</span>
                            </label>

                            {picked && (
                              <label
                                className="flex cursor-pointer items-center gap-1.5 text-[11.5px]"
                                title="Untick for an opened strip: still refunded, but never re-sold"
                              >
                                <Checkbox
                                  checked={draft?.restock ?? true}
                                  onCheckedChange={(v) => setRestock(line.id, v === true)}
                                />
                                <span className="inline-flex items-center gap-1">
                                  {(draft?.restock ?? true) ? <Package size={12} /> : <PackageX size={12} className="text-warn-ink" />}
                                  Back on the shelf
                                </span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!fullyReturned && (
                  <>
                    <FormField label="Reason for the return" required className="mt-3.5">
                      <Input
                        value={reason}
                        placeholder="e.g. wrong strength, patient reaction, duplicate purchase"
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </FormField>

                    <div className="mb-3 space-y-3">
                      <PaymentModePicker
                        label="Refund payment mode"
                        value={refundMode}
                        onChange={setRefundMode}
                      />
                      <PaymentStatusPicker
                        label="Refund status"
                        value={refundStatus}
                        onChange={setRefundStatus}
                        paidHint="Money handed back now"
                        pendingHint="Owed — settle it later"
                      />
                    </div>

                    {selected.length > 0 && (
                      <div className="mb-3 rounded-lg bg-canvas p-3">
                        <TotalLine label="Refund" value={estimatedRefund} tone="grand" />
                        {(sale.loyalty_points_earned > 0 || sale.loyalty_points_redeemed > 0) && (
                          <ul className="mt-2 space-y-0.5 border-t border-line pt-2 text-[11px] text-ink-muted">
                            {sale.loyalty_points_earned > 0 && (
                              <li>Points earned on the returned items are taken back.</li>
                            )}
                            {sale.loyalty_points_redeemed > 0 && (
                              <li>The share paid with points comes back as points, not cash.</li>
                            )}
                          </ul>
                        )}
                      </div>
                    )}

                    <Button
                      variant="danger"
                      full
                      disabled={createReturn.isPending || selected.length === 0}
                      loading={createReturn.isPending}
                      onClick={submitReturn}
                    >
                      {!createReturn.isPending && <Undo2 size={15} />}
                      {createReturn.isPending
                        ? 'Recording…'
                        : selected.length === 0
                        ? 'Select what is coming back'
                        : refundStatus === 'Pending'
                        ? `Record ${money(estimatedRefund)} as owed`
                        : `Refund ${money(estimatedRefund)} by ${refundMode}`}
                    </Button>
                  </>
                )}

                {sale.returns.length > 0 && (
                  <div className="mt-4 border-t border-line pt-3">
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                      <History size={12} /> Returns against this bill
                    </div>
                    <div className="space-y-1.5">
                      {sale.returns.map((r) => (
                        <div key={r.id} className="rounded-lg border border-border px-3 py-2 text-[12px]">
                          <div className="flex justify-between gap-2 font-semibold">
                            <span className="font-mono">{r.return_number}</span>
                            <span className="tabular-nums text-danger-ink">− {money(r.refund_amount)}</span>
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant={r.refund_status === 'Paid' ? 'success' : 'warn'}>
                              {r.refund_status === 'Paid' ? `Refunded · ${r.refund_mode}` : `Owed · ${r.refund_mode}`}
                            </Badge>
                            {r.points_reversed > 0 && <Badge variant="neutral">−{r.points_reversed} pts</Badge>}
                            {r.points_refunded > 0 && <Badge variant="neutral">+{r.points_refunded} pts back</Badge>}
                          </div>
                          <div className="mt-0.5 text-[11px] text-ink-muted">
                            {shortDateTime(r.returned_at)} · {r.reason} · by {r.returned_by}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        )}
      </div>

      {/* ── Outstanding refunds ─────────────────────────────────── */}
      {/* Not date-filtered, and placed ABOVE the day's returns on purpose:
          this is money the hospital owes people, which is the one thing on
          this screen that must not be scrolled past. */}
      {pending.length > 0 && (
        <Card className="border-warn-soft">
          <CardHeader>
            <CardTitle><Clock size={15} /> Refunds still owed</CardTitle>
            <Badge variant="warn">{money(pendingTotal)} across {pending.length}</Badge>
          </CardHeader>
          <div className="space-y-1.5">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 text-[12.5px] font-semibold">
                    <span className="font-mono">{r.return_number}</span>
                    <span className="font-normal text-ink-muted">{r.patient_name}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-ink-muted">
                    {shortDateTime(r.returned_at)} · against <span className="font-mono">{r.bill_number}</span> · {r.reason}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[13px] font-bold tabular-nums">{money(r.refund_amount)}</div>
                  <div className="text-[10.5px] text-ink-muted">by {r.refund_mode}</div>
                </div>
                <Button
                  size="sm"
                  className="shrink-0"
                  disabled={settleRefund.isPending}
                  onClick={() => settleRefund.mutate({ returnId: r.id })}
                >
                  <Wallet size={13} /> Mark refunded
                </Button>
              </div>
            ))}
          </div>
          {settleError && (
            <div className="mt-2.5 rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{settleError}</div>
          )}
          <p className="mt-2.5 text-[11px] text-ink-muted">
            The goods and any points already moved when the return was recorded — only the money is outstanding.
          </p>
        </Card>
      )}

      {/* ── The day's returns ───────────────────────────────────── */}
      {returns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle><RotateCcw size={15} /> Returns on this date</CardTitle>
            <Badge variant="neutral">{returns.length}</Badge>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Return</TableHead>
                  <TableHead>Against</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Refunded by</TableHead>
                  <TableHead className="text-right">Refund</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-[11.5px] font-semibold">{r.return_number}</TableCell>
                    <TableCell className="font-mono text-[11.5px]">{r.bill_number}</TableCell>
                    <TableCell>{r.patient_name}</TableCell>
                    <TableCell className="text-[12px] text-ink-muted">{r.reason}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px]">{r.refund_mode}</span>
                        <Badge variant={r.refund_status === 'Paid' ? 'success' : 'warn'}>
                          {r.refund_status === 'Paid' ? 'Paid' : 'Owed'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{money(r.refund_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
